import {
  ALLOYS,
  HEAT_PROFILES,
  LATTICE_METALS,
  METAL_MELTING_POINTS,
  SUBSTITUTIONAL_RADIUS_LIMIT_PERCENT,
  radiusDifferencePercent,
  type HeatProcess,
  type LatticeMetal,
  type MaterialsVisual,
} from './materialsChemistry';
import { ReactivityLadder, SizeScale, SubdivisionSquare } from './MaterialsChemistryScaleVisuals';

const GOLDEN_FRACTION = 0.618034;
const PERCENT = 100;

const SUPERSCRIPT_CHARGES: Record<string, string> = { '+': '⁺', '2+': '²⁺', '3+': '³⁺' };

const LATTICE = { cols: 6, rows: 4, spacing: 44, left: 34, top: 30 } as const;
const LATTICE_WIDTH = LATTICE.left * 2 + LATTICE.spacing * (LATTICE.cols - 1) + LATTICE.spacing / 2;
const LATTICE_HEIGHT = LATTICE.top * 2 + LATTICE.spacing * (LATTICE.rows - 1);
const LATTICE_ION_BASE_RADIUS = 8;
const LATTICE_ION_SCALE = 8;
const REFERENCE_ION_PM = 102;
const SLID_ROWS = 2;
const ELECTRON_RADIUS = 2.4;

function fraction(value: number): number {
  return value - Math.floor(value);
}

function latticeX(col: number, row: number, shifted: boolean): number {
  return LATTICE.left + col * LATTICE.spacing + (shifted && row < SLID_ROWS ? LATTICE.spacing / 2 : 0);
}

function MetalLattice({ metal, shifted }: { metal: LatticeMetal; shifted: boolean }) {
  const data = LATTICE_METALS[metal];
  const radius = LATTICE_ION_BASE_RADIUS + (data.ionicRadiusPm / REFERENCE_ION_PM) * LATTICE_ION_SCALE;
  const cations = LATTICE.cols * LATTICE.rows;
  const electrons = cations * data.valenceElectrons;
  const ionLabel = `${data.symbol}${SUPERSCRIPT_CHARGES[data.ionCharge]}`;
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${LATTICE_WIDTH} ${LATTICE_HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`Metallic bonding model of ${data.name.toLowerCase()}: ${cations} ${ionLabel} ions (${data.ionicRadiusPm} pm) in a sea of ${electrons} delocalised electrons, ${data.valenceElectrons} per atom.${shifted ? ' The top two layers have slid half a spacing; every ion is still surrounded by electrons.' : ''}`}
      >
        {Array.from({ length: cations }, (_, index) => {
          const col = index % LATTICE.cols;
          const row = Math.floor(index / LATTICE.cols);
          return (
            <g key={index}>
              <circle cx={latticeX(col, row, shifted)} cy={LATTICE.top + row * LATTICE.spacing} r={radius} className="fill-wash-coral stroke-brand-coral" strokeWidth="1.5" />
              <text x={latticeX(col, row, shifted)} y={LATTICE.top + row * LATTICE.spacing + 3} fontSize="8" fontWeight="800" textAnchor="middle" className="fill-ink">
                {ionLabel}
              </text>
            </g>
          );
        })}
        {Array.from({ length: electrons }, (_, index) => {
          const x = LATTICE.left / 2 + fraction(GOLDEN_FRACTION * (index + 1) + 0.21) * (LATTICE_WIDTH - LATTICE.left);
          const y = LATTICE.top / 2 + fraction(GOLDEN_FRACTION * 2.3 * (index + 1) + 0.37) * (LATTICE_HEIGHT - LATTICE.top);
          return <circle key={`e-${index}`} cx={x} cy={y} r={ELECTRON_RADIUS} className="fill-ink" opacity="0.75" />;
        })}
        {shifted ? (
          <text x={LATTICE.left} y={LATTICE.top - 16} fontSize="9" fontWeight="800" className="fill-ink">
            top layers slid → (force applied)
          </text>
        ) : null}
      </svg>
      <figcaption className="mt-2 text-xs font-medium leading-5 text-slate2">
        Circles labelled {ionLabel} are cations; small dots are delocalised electrons ({data.valenceElectrons} per atom).
        Two-dimensional slice, not to scale between diagrams.
      </figcaption>
    </figure>
  );
}

const IONIC = { cols: 6, rows: 4, spacing: 30, left: 24, top: 24 } as const;
const IONIC_WIDTH = IONIC.left * 2 + IONIC.spacing * IONIC.cols;
const IONIC_HEIGHT = IONIC.top * 2 + IONIC.spacing * (IONIC.rows - 1);

function IonicLattice({ shifted }: { shifted: boolean }) {
  const slip = shifted ? 1 : 0;
  const charge = (row: number, col: number) => ((row + col) % 2 === 0 ? 'Na⁺' : 'Cl⁻');
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${IONIC_WIDTH} ${IONIC_HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`Sodium chloride lattice of alternating Na⁺ and Cl⁻ ions.${shifted ? ' After the top layers slide one ion along, like charges sit next to each other across the slip line and repel, so the crystal splits.' : ''}`}
      >
        {Array.from({ length: IONIC.cols * IONIC.rows }, (_, index) => {
          const col = index % IONIC.cols;
          const row = Math.floor(index / IONIC.cols);
          const x = IONIC.left + col * IONIC.spacing + (row < SLID_ROWS ? slip * IONIC.spacing : 0);
          const label = charge(row, col);
          const positive = label === 'Na⁺';
          return (
            <g key={index}>
              <circle cx={x} cy={IONIC.top + row * IONIC.spacing} r={positive ? 9 : 12} className={positive ? 'fill-wash-coral stroke-brand-coral' : 'fill-wash-sky stroke-brand-sky'} strokeWidth="1.5" />
              <text x={x} y={IONIC.top + row * IONIC.spacing + 3} fontSize="7" fontWeight="800" textAnchor="middle" className="fill-ink">
                {label}
              </text>
            </g>
          );
        })}
        {shifted
          ? Array.from({ length: IONIC.cols - 1 }, (_, index) => {
              const x = IONIC.left + (index + 1) * IONIC.spacing;
              const y1 = IONIC.top + (SLID_ROWS - 1) * IONIC.spacing + 12;
              const y2 = IONIC.top + SLID_ROWS * IONIC.spacing - 12;
              return <line key={index} x1={x} x2={x} y1={y1} y2={y2} className="stroke-brand-coral" strokeWidth="2" strokeDasharray="2 2" />;
            })
          : null}
      </svg>
      <figcaption className="mt-2 text-xs font-medium leading-5 text-slate2">
        Sodium chloride for comparison.{shifted ? ' Dashed lines mark like charges meeting across the slip line.' : ''}
      </figcaption>
    </figure>
  );
}

function MeltingBars({ metals, revealed }: { metals: string[]; revealed: boolean }) {
  const rows = metals.flatMap((symbol) => {
    const data = METAL_MELTING_POINTS[symbol];
    return data ? [{ symbol, ...data }] : [];
  });
  const max = Math.max(...rows.map((row) => row.meltingPointC));
  return (
    <figure className="min-w-0" aria-label="Melting points of the metals in this case">
      <ul className="grid gap-2">
        {rows.map((row) => (
          <li
            key={row.symbol}
            className="grid grid-cols-[6rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-xs font-bold text-ink-soft"
            aria-label={revealed ? `${row.name}: melting point ${row.meltingPointC} °C` : `${row.name}: melting point hidden until you check`}
          >
            <span className="font-extrabold text-ink">{row.name}</span>
            <span className="relative h-5 rounded-full bg-surface" aria-hidden="true">
              {revealed ? (
                <span className="absolute inset-y-0.5 left-0 rounded-full bg-brand-coral" style={{ width: `${(row.meltingPointC / max) * PERCENT}%` }} />
              ) : null}
            </span>
            <span className="text-right">{revealed ? `${row.meltingPointC} °C` : '?'}</span>
          </li>
        ))}
      </ul>
      <figcaption className="mt-3 text-xs font-medium leading-5 text-slate2">
        Melting points at 101.325 kPa{revealed ? '' : ' appear after you check'}.
      </figcaption>
    </figure>
  );
}

const ALLOY_GRID = { cols: 7, rows: 4, spacing: 40, left: 26, top: 26, hostRadius: 16 } as const;
const ALLOY_WIDTH = ALLOY_GRID.left * 2 + ALLOY_GRID.spacing * (ALLOY_GRID.cols - 1);
const ALLOY_HEIGHT = ALLOY_GRID.top * 2 + ALLOY_GRID.spacing * (ALLOY_GRID.rows - 1);
const SUBSTITUTION_SITES = [3, 9, 15, 18, 24];
const INTERSTITIAL_SITES: [number, number][] = [[0, 0], [2, 1], [4, 0], [1, 2], [5, 2]];
const UNKNOWN_RADIUS_RATIO = 1.08;

function AlloyLattice({ alloyId, revealed }: { alloyId: string; revealed: boolean }) {
  const alloy = ALLOYS[alloyId];
  if (!alloy) return null;
  const substitutes = alloy.additives.filter((component) => component.kind === 'substitutional');
  const interstitial = alloy.additives.find((component) => component.kind === 'interstitial');
  const radiusFor = (radiusPm: number | undefined) =>
    radiusPm !== undefined && alloy.hostRadiusPm !== undefined
      ? ALLOY_GRID.hostRadius * (radiusPm / alloy.hostRadiusPm)
      : ALLOY_GRID.hostRadius * UNKNOWN_RADIUS_RATIO;
  const siteAdditive = (index: number) => {
    if (!revealed || substitutes.length === 0) return null;
    const slot = SUBSTITUTION_SITES.indexOf(index);
    return slot < 0 ? null : substitutes[slot % substitutes.length];
  };
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${ALLOY_WIDTH} ${ALLOY_HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={
          revealed
            ? `${alloy.name}: ${alloy.host} lattice with ${alloy.additives.map((component) => `${component.symbol} (${component.kind})`).join(', ')}.`
            : `Pure ${alloy.host} lattice before the other elements are added.`
        }
      >
        {Array.from({ length: ALLOY_GRID.cols * ALLOY_GRID.rows }, (_, index) => {
          const col = index % ALLOY_GRID.cols;
          const row = Math.floor(index / ALLOY_GRID.cols);
          const additive = siteAdditive(index);
          const x = ALLOY_GRID.left + col * ALLOY_GRID.spacing;
          const y = ALLOY_GRID.top + row * ALLOY_GRID.spacing;
          return (
            <g key={index}>
              <circle
                cx={x}
                cy={y}
                r={additive ? radiusFor(additive.radiusPm) : ALLOY_GRID.hostRadius}
                className={additive ? 'fill-wash-coral stroke-brand-coral' : 'fill-wash-sky stroke-brand-sky'}
                strokeWidth={additive ? 2.5 : 1.5}
              />
              <text x={x} y={y + 3} fontSize="9" fontWeight="800" textAnchor="middle" className="fill-ink">
                {additive ? additive.symbol : alloy.host}
              </text>
            </g>
          );
        })}
        {revealed && interstitial
          ? INTERSTITIAL_SITES.map(([col, row]) => {
              const x = ALLOY_GRID.left + (col + 0.5) * ALLOY_GRID.spacing;
              const y = ALLOY_GRID.top + (row + 0.5) * ALLOY_GRID.spacing;
              return (
                <g key={`${col}-${row}`}>
                  <circle cx={x} cy={y} r={radiusFor(interstitial.radiusPm)} className="fill-ink" />
                  <text x={x} y={y + 3} fontSize="7" fontWeight="800" textAnchor="middle" className="fill-white">
                    {interstitial.symbol}
                  </text>
                </g>
              );
            })
          : null}
      </svg>
      <figcaption className="mt-2 space-y-1 text-xs font-medium leading-5 text-slate2">
        <p>
          {alloy.name}: {alloy.composition}. {revealed ? 'Added atoms are outlined in a thicker ring and labelled.' : 'The added atoms appear after you check.'}
        </p>
        <ul className="grid gap-0.5">
          {alloy.additives.map((component) => {
            const difference = radiusDifferencePercent(alloy, component);
            return (
              <li key={component.symbol}>
                {component.symbol}
                {component.radiusPm !== undefined ? ` ${component.radiusPm} pm` : ' (similar size; no value recorded)'} vs{' '}
                {alloy.host}
                {alloy.hostRadiusPm !== undefined ? ` ${alloy.hostRadiusPm} pm` : ''}
                {difference !== null ? ` — ${Math.round(difference)} % different` : ''}
              </li>
            );
          })}
          <li>Guide: within about {SUBSTITUTIONAL_RADIUS_LIMIT_PERCENT} % favours substitution. Atom sizes are drawn in proportion to these radii.</li>
        </ul>
      </figcaption>
    </figure>
  );
}

const HEAT_VIEW = { width: 300, height: 140, left: 30, right: 10, top: 12, bottom: 28 } as const;
const HEAT_TIME_MAX = 10;

function HeatProfileChart({ process, revealed }: { process: HeatProcess; revealed: boolean }) {
  const profile = HEAT_PROFILES[process];
  const plotWidth = HEAT_VIEW.width - HEAT_VIEW.left - HEAT_VIEW.right;
  const plotHeight = HEAT_VIEW.height - HEAT_VIEW.top - HEAT_VIEW.bottom;
  const points = profile.points
    .map(([time, temperature]) => `${HEAT_VIEW.left + (time / HEAT_TIME_MAX) * plotWidth},${HEAT_VIEW.top + (1 - temperature) * plotHeight}`)
    .join(' ');
  return (
    <figure className="min-w-0">
      <svg viewBox={`0 0 ${HEAT_VIEW.width} ${HEAT_VIEW.height}`} className="w-full" role="img" aria-label={`Schematic temperature–time profile for ${profile.label.toLowerCase()}.`}>
        <line x1={HEAT_VIEW.left} x2={HEAT_VIEW.left} y1={HEAT_VIEW.top} y2={HEAT_VIEW.height - HEAT_VIEW.bottom} className="stroke-ink" strokeWidth="1.5" />
        <line x1={HEAT_VIEW.left} x2={HEAT_VIEW.width - HEAT_VIEW.right} y1={HEAT_VIEW.height - HEAT_VIEW.bottom} y2={HEAT_VIEW.height - HEAT_VIEW.bottom} className="stroke-ink" strokeWidth="1.5" />
        <polyline points={points} fill="none" className="stroke-brand-coral" strokeWidth="3" strokeLinejoin="round" />
        <text x={HEAT_VIEW.left + plotWidth / 2} y={HEAT_VIEW.height - 8} fontSize="9" fontWeight="700" textAnchor="middle" className="fill-ink">
          time →
        </text>
        <text x="12" y={HEAT_VIEW.top + plotHeight / 2} fontSize="9" fontWeight="700" textAnchor="middle" className="fill-ink" transform={`rotate(-90 12 ${HEAT_VIEW.top + plotHeight / 2})`}>
          temperature
        </text>
      </svg>
      <figcaption className="mt-2 text-xs font-medium leading-5 text-slate2">
        {profile.label} — schematic, no scale.{revealed ? ` Result: ${profile.outcome}.` : ''}
      </figcaption>
    </figure>
  );
}

export function MaterialsVisualView({ visual, revealed }: { visual: MaterialsVisual; revealed: boolean }) {
  switch (visual.type) {
    case 'lattice':
      return (
        <div className="grid gap-4">
          <MetalLattice metal={visual.metal} shifted={visual.shifted && revealed} />
          {visual.compareIonic ? <IonicLattice shifted={revealed} /> : null}
        </div>
      );
    case 'melting':
      return <MeltingBars metals={visual.metals} revealed={revealed} />;
    case 'alloy':
      return <AlloyLattice alloyId={visual.alloyId} revealed={revealed} />;
    case 'heat':
      return <HeatProfileChart process={visual.process} revealed={revealed} />;
    case 'ladder':
      return <ReactivityLadder metal={visual.metal} revealed={revealed} />;
    case 'size-scale':
      return <SizeScale sizeNm={visual.sizeNm} label={visual.label} revealed={revealed} />;
    case 'subdivision':
      return <SubdivisionSquare perEdge={visual.perEdge} caption={visual.caption} />;
    default:
      return null;
  }
}
